import type { Board, GeometryElement } from 'jsxgraph';
import type { InferActionType } from '../../model';
import type { ActionDefinition, ActionResult, TransitionProfile } from '../../model/widget-action.model';
import { type BaseWidget, WidgetEvent, type WidgetEventCallback } from '../../model/widget-base.model';
import type { WidgetConfig, WidgetState } from '../../model/widget-config.model';
import {
  animateValue,
  type AnimationResult,
  type ParameterizedElement,
  animateBoundingBox as animateBoundingBoxUtil,
  type BoundingBoxAnimationContext,
  delay,
  resolveTransition,
  type BuiltInActionContext,
  focusRegion,
  transformView,
  patchDisplay,
  highlight,
  resetView,
  updateParam,
} from './actions';
import { freeBoard, initBoard, type BoardInitOptions } from './board';
import { boundingBoxToTuple } from './coordinate-system';
import { createCoordinateSystem as createCoordinateSystemDisplay, type CoordinateSystemConfig } from './elements';
import { WidgetEventEmitter } from './events';
import type { JsxgraphThemeService } from './jsxgraph-theme.service';
import type {
  InteractionFeatureConfig,
  JsxgraphActionType,
  JsxgraphBoardOptions,
  JsxgraphStatic,
} from './jsxgraph.model';
import { DEFAULT_DISPLAY_CONFIG } from './jsxgraph.model';

// Re-export for convenience
export type { BuiltInActionContext };

/**
 * Abstract base class for JSXGraph-based widgets.
 * Handles common functionality like board management, events, animations, and theming.
 *
 * This class builds JSXGraph API options internally by combining:
 * - Feature configs (what to show)
 * - Theme values (how it looks)
 *
 * Implements BuiltInActionContext to allow action functions to receive the widget
 * instance directly instead of a context bag.
 */
export abstract class JsxgraphBaseWidget<Config extends WidgetConfig = WidgetConfig>
  implements BaseWidget<Config>, BuiltInActionContext
{
  constructor(
    protected container: HTMLElement,
    jsxInstance: JsxgraphStatic,
    readonly themeService: JsxgraphThemeService,
  ) {
    this.jsxInstance = jsxInstance;
  }

  abstract id: string;
  abstract type: Config['type'];
  board!: Board;
  protected jsxInstance: JsxgraphStatic;
  readonly objects: Map<string, GeometryElement> = new Map();
  isDestroyed = false;
  private isAnimating = false;
  protected instanceTransitions: Record<string, TransitionProfile> =
    {}; /** Instance-level transition overrides from behavior config */

  /**
   * Registry of elements that support parameterized updates (UPDATE_PARAM action).
   * Subclasses register elements here during init() to enable animated parameter updates.
   */
  readonly parameterizedElements: Map<string, ParameterizedElement> = new Map();

  /** Event emitter for widget event pub/sub */
  private readonly eventEmitter = new WidgetEventEmitter();

  // ==============================================================================
  // LIFECYCLE
  // ==============================================================================

  abstract init(config: Config): void;

  protected abstract getCurrentConfig(): Config | undefined;

  abstract getState(): WidgetState;

  update(config: Partial<Config>): void {
    const currentConfig = this.getCurrentConfig();
    if (!currentConfig) {
      console.error('Cannot update widget: no config available');
      return;
    }

    const newConfig = { ...currentConfig, ...config };
    this.destroy();
    this.init(newConfig);
    this.emit(WidgetEvent.StateChange, this.getState());
  }

  setState(state: WidgetState): void {
    this.destroy();
    this.init(state as unknown as Config);
  }

  destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    if (this.board && this.jsxInstance) {
      freeBoard(this.board, this.jsxInstance);
    }

    this.objects.clear();
    this.eventEmitter.clear();
    this.parameterizedElements.clear();
    this.isDestroyed = true;

    (this as unknown as { board: Board | null }).board = null;
  }

  // ==============================================================================
  // EVENT SUBSCRIPTION (delegated to WidgetEventEmitter)
  // ==============================================================================

  on(event: string, callback: WidgetEventCallback): void {
    this.eventEmitter.on(event, callback);
  }

  off(event: string, callback: WidgetEventCallback): void {
    this.eventEmitter.off(event, callback);
  }

  protected emit(event: string, data: unknown): void {
    this.eventEmitter.emit(event, data);
  }

  // ==============================================================================
  // ACTION SYSTEM
  // ==============================================================================

  async executeAction(action: ActionDefinition<InferActionType<Config>>): Promise<ActionResult> {
    if (this.isDestroyed) {
      return { success: false, error: 'Widget has been destroyed' };
    }

    const transition = resolveTransition(action.transition, this.instanceTransitions);

    if (transition?.delay && transition.delay > 0) {
      await delay(transition.delay);
    }

    const result = await this.performAction(action.action, action.payload, transition);
    if (result.success) {
      this.emit(WidgetEvent.AnimationComplete, { action: action.action, payload: action.payload });
    }
    return result;
  }

  protected async performAction(
    action: InferActionType<Config>,
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    switch (action as JsxgraphActionType) {
      case 'FOCUS_REGION':
        return focusRegion(this, payload, transition);
      case 'TRANSFORM_VIEW':
        return transformView(this, payload, transition);
      case 'PATCH_DISPLAY':
        return patchDisplay(this, payload);
      case 'HIGHLIGHT':
        return highlight(this, payload, transition);
      case 'RESET_VIEW':
        return resetView(this, boundingBoxToTuple(DEFAULT_DISPLAY_CONFIG.boundingbox!), transition);
      case 'UPDATE_PARAM':
        return updateParam(this, payload, transition);
      default:
        return { success: false, error: `Unknown action '${action}'` };
    }
  }

  // ==============================================================================
  // ANIMATION UTILITIES
  // ==============================================================================

  protected runAnimatedValue(
    startValue: number,
    endValue: number,
    transition: TransitionProfile | undefined,
    onStep: (value: number) => void,
  ): Promise<AnimationResult> {
    return animateValue({
      startValue,
      endValue,
      transition: transition ?? { duration: 0, easing: 'linear', type: 'none' },
      onStep,
      shouldAbort: () => this.isDestroyed || !this.board,
    });
  }

  protected registerParameterizedElement(id: string, element: ParameterizedElement): void {
    this.parameterizedElements.set(id, element);
  }

  animateBoundingBox(bbox: [number, number, number, number], transition?: TransitionProfile): Promise<void> {
    const ctx: BoundingBoxAnimationContext = {
      board: this.board,
      isAnimating: this.isAnimating,
      isDestroyed: this.isDestroyed,
      setAnimating: (value) => {
        this.isAnimating = value;
      },
    };
    return animateBoundingBoxUtil(ctx, bbox, transition);
  }

  getRegionBoundingBox(_region: string | number): [number, number, number, number] | null {
    return null;
  }

  onParamAnimationStep(): void {
    // Base: no-op. Subclasses override for per-step reactions.
  }

  // ==============================================================================
  // BOARD INITIALIZATION (delegates to board/ module)
  // ==============================================================================

  protected initBoard(
    options: JsxgraphBoardOptions = {},
    interaction?: InteractionFeatureConfig | boolean,
    backgroundColor?: string,
  ): void {
    if (this.board && this.jsxInstance) {
      freeBoard(this.board, this.jsxInstance);
    }

    this.isDestroyed = false;

    const initOptions: BoardInitOptions = {
      container: this.container,
      widgetId: this.id,
      jsxInstance: this.jsxInstance,
      themeService: this.themeService,
      options,
      interaction,
      backgroundColor,
    };

    this.board = initBoard(initOptions);
    this.emit(WidgetEvent.Ready, { boardId: this.container.id });
  }

  // ==============================================================================
  // COORDINATE SYSTEM CREATION
  // ==============================================================================

  protected createCoordinateSystem(config?: CoordinateSystemConfig): void {
    if (!this.board) {
      return;
    }

    const { objects } = createCoordinateSystemDisplay({
      board: this.board,
      themeService: this.themeService,
      config,
    });

    objects.forEach((obj, key) => {
      this.objects.set(key, obj);
    });
  }

  // Core elements are created through a factory not directly through the base
}
